/**
 * A thumb ring. Reports a direction while held, nothing when not. The
 * walking itself is the sim's: `App` turns the vector into `move` actions.
 */

import { useRef, useState } from 'react';

interface Props {
  /** Unit vector (or shorter) while dragging; null on release. */
  readonly onVector: (v: { x: number; y: number } | null) => void;
}

const RADIUS = 46;
const KNOB_TRAVEL = 28;

export function Joystick({ onVector }: Props) {
  const ring = useRef<HTMLDivElement>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });

  const update = (clientX: number, clientY: number) => {
    const el = ring.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = (clientX - cx) / RADIUS;
    let dy = (clientY - cy) / RADIUS;
    const len = Math.hypot(dx, dy);
    if (len > 1) {
      dx /= len;
      dy /= len;
    }
    setKnob({ x: dx * KNOB_TRAVEL, y: dy * KNOB_TRAVEL });
    onVector(len < 0.15 ? null : { x: dx, y: dy });
  };

  const release = () => {
    setKnob({ x: 0, y: 0 });
    onVector(null);
  };

  return (
    <div
      ref={ring}
      className="stick"
      role="slider"
      aria-label="Walk"
      aria-valuenow={0}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX, e.clientY);
      }}
      onPointerMove={(e) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) update(e.clientX, e.clientY);
      }}
      onPointerUp={release}
      onPointerCancel={release}
    >
      <div className="knob" style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }} />
    </div>
  );
}
